import { Text, View } from "react-native";
import { useResidentContext } from "../../provider/ResidentProvider";
import { useGetResidentStatusHistoryQuery } from "app/services/api/residentsApi";
import {
  ResidentStatusEnum,
  StatusIndicatorMapping,
  StatusTitleMapping,
} from "app/services/api/types/residents/enums/ResidentStatusEnum";
import StatusIndicator from "ui/atoms/avatar/statusIndicator/StatusIndicator";
import { StatusIndicatorType } from "ui/atoms/avatar/statusIndicator/interfaces";
import { formatDate } from "ui/utils/dateUtils";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";

const dotColorMap: Record<StatusIndicatorType, string> = {
  active: "bg-success-600",
  warning: "bg-warning-500",
  draft: "bg-warning-500",
  danger: "bg-error-600",
  default: "bg-gray-400",
};

export default function ResidentStatusHistoryTemplate() {
  const { residentId } = useResidentContext();

  const { data: statusHistory, isLoading } = useGetResidentStatusHistoryQuery(
    { residentId: residentId! },
    { skip: !residentId },
  );

  if (isLoading) {
    return (
      <View className="flex-1 justify-center items-center my-10">
        <LoadingSpinner />
      </View>
    );
  }

  if (!statusHistory?.length) {
    return (
      <Text className="text-typography-600">No status history available.</Text>
    );
  }

  return (
    <View className="resident-container gap-4">
      {statusHistory.map((entry, index) => {
        const isLatest = index === 0;
        const status = entry.name as ResidentStatusEnum;
        const indicatorType = StatusIndicatorMapping[status];
        const statusTitle = StatusTitleMapping[status] ?? entry.name;

        return (
          <View
            key={entry.id}
            className={`rounded-lg p-4 gap-2 border ${isLatest ? "bg-primary-50 border-primary-200" : "bg-secondary-0 border-transparent"}`}
          >
            <View className="flex-row items-center justify-between">
              <View className="flex-row items-center gap-2">
                <View
                  className={`w-2.5 h-2.5 rounded-full ${dotColorMap[indicatorType] ?? "bg-gray-400"}`}
                />
                <StatusIndicator
                  status={indicatorType}
                  statusTitle={statusTitle}
                />
              </View>
              {isLatest ? (
                <View className="bg-primary-500 rounded-full px-2 py-0.5">
                  <Text className="text-white text-xs font-semibold">
                    Current
                  </Text>
                </View>
              ) : null}
            </View>
            {entry.reason ? (
              <View>
                <Text className="text-primary-400 font-medium mt-2 mb-1">
                  Reason
                </Text>
                <Text className="text-typography-700">{entry.reason}</Text>
              </View>
            ) : null}
            {entry.created_at ? (
              <Text className="text-typography-500 text-sm mt-1">
                {formatDate(entry.created_at)}
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}
